import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { businessService, categoryService, customMenuService, popupService, statsService } from '../services/api';
import { useMenuTimeTracking } from '../hooks/useMenuTimeTracking';
import { useProductEvents } from '../hooks/useSocket';
import SharedFooter from '../components/SharedFooter';
import PopupRenderer from '../components/PopupRenderer';
import { useCart } from '../contexts/CartContext';
import './PublicMenu.css';

// URL base per le risorse statiche
const API_BASE_URL = (typeof window !== 'undefined' ? `${window.location.origin}/api` : ((process.env.REACT_APP_API_URL as string | undefined) || '/api'));

interface BusinessInfo {
  id: number;
  name: string;
  description?: string;
  logo_path?: string;
  address?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
  instagram?: string;
  facebook?: string;
  whatsapp?: string;
  asporto_enabled?: boolean | number;
}

interface Category {
  id: number;
  name: string;
  name_en?: string;
  description?: string;
  description_en?: string;
  parent_id: number | null;
  is_active: number;
  parent_name?: string;
  sort_order?: number;
}

const PublicMenu: React.FC = () => {
  const navigate = useNavigate();
  const [businessInfo, setBusinessInfo] = useState<BusinessInfo | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [language, setLanguage] = useState<'it' | 'en'>(
    (typeof window !== 'undefined' && localStorage.getItem('menu-language') === 'en') ? 'en' : 'it'
  );
  const [visibleCustom, setVisibleCustom] = useState<{ id: number; name: string } | null>(null);
  const [activePopups, setActivePopups] = useState<any[]>([]);
  const [currentPopup, setCurrentPopup] = useState<any>(null);
  const { setAsportoEnabled, setWhatsappNumber, setBusinessName } = useCart();

  // Funzione per ricaricare le categorie
  const reloadCategories = useCallback(async () => {
    try {
      if (process.env.NODE_ENV !== 'production') {
        console.log('Ricaricamento categorie in corso...');
      }
      const categoriesResponse = await categoryService.getPublic();
      if (categoriesResponse.success) {
        const cats = categoriesResponse.data.categories || [];
        cats.sort((a: Category, b: Category) => {
          const ao = a.sort_order ?? 0;
          const bo = b.sort_order ?? 0;
          if (ao !== bo) return ao - bo;
          return (a.name || '').localeCompare(b.name || '');
        });
        setCategories(cats);
        if (process.env.NODE_ENV !== 'production') {
          console.log('Categorie ricaricate con successo');
        }
      }
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Errore nel ricaricamento delle categorie:', err);
      }
    }
  }, []);

  const reloadCustomMenus = useCallback(async () => {
    try {
      const cm = await customMenuService.getAll();
      const menus = cm?.data?.menus || [];
      const firstVisible = menus.find((m: any) => m.is_visible);
      setVisibleCustom(firstVisible ? { id: firstVisible.id, name: firstVisible.name } : null);
    } catch { /* ignore */ }
  }, []);

  // Gestione eventi Socket.IO per aggiornamenti in tempo reale
  useProductEvents({
    onProductAdded: () => reloadCategories(),
    onProductUpdated: () => reloadCategories(),
    onProductDeleted: () => reloadCategories(),
    onCustomMenuUpdated: () => reloadCustomMenus(),
  });

  useMenuTimeTracking();

  useEffect(() => {
    // Conta una visita per dispositivo per giorno (localStorage svuota le chiavi vecchie automaticamente)
    const today = new Date().toISOString().split('T')[0]; // es. '2026-05-27'
    const visitKey = `menu_visited_${today}`;
    if (!localStorage.getItem(visitKey)) {
      localStorage.setItem(visitKey, '1');
      statsService.trackMenuView().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch business info
        const businessResponse = await businessService.get();
        setBusinessInfo(businessResponse);
        // Sync CartContext con impostazioni asporto
        setAsportoEnabled(Boolean(businessResponse?.asporto_enabled));
        setWhatsappNumber(businessResponse?.whatsapp || '');
        setBusinessName(businessResponse?.name || '');

        // Fetch popups
        try {
          const popupsResponse = await popupService.getActive();
          if (popupsResponse.success) {
            setActivePopups(popupsResponse.data.popups);
          }
        } catch (e) {
          console.error('Error fetching popups:', e);
        }

        // Fetch public categories (only parent categories with products)
        const categoriesResponse = await categoryService.getPublic();
        if (categoriesResponse.success) {
          const cats = categoriesResponse.data.categories || [];
          cats.sort((a: Category, b: Category) => {
            const ao = a.sort_order ?? 0;
            const bo = b.sort_order ?? 0;
            if (ao !== bo) return ao - bo;
            return (a.name || '').localeCompare(b.name || '');
          });
          setCategories(cats);
        } else {
          setError('Errore nel caricamento delle categorie');
        }

        // Fetch custom menus to detect visible one
        try {
          const cm = await customMenuService.getAll();
          const menus = cm?.data?.menus || [];
          const firstVisible = menus.find((m: any) => m.is_visible);
          if (firstVisible) {
            setVisibleCustom({ id: firstVisible.id, name: firstVisible.name });
          } else {
            setVisibleCustom(null);
          }
        } catch { /* ignore */ }
      } catch (err) {
        console.error('Errore nel caricamento dei dati:', err);
        setError('Errore nel caricamento del menu');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCategoryClick = (category: Category) => {
    navigate(`/menu/category/${category.id}`);
  };

  useEffect(() => {
    if (activePopups.length > 0) {
      // Logic to show popups
      // Simple logic: show first onload popup
      const onloadPopup = activePopups.find(p => p.trigger_type === 'onload');
      if (onloadPopup) {
        // Check frequency
        if (onloadPopup.frequency === 'once_per_session') {
          const seen = sessionStorage.getItem(`popup_seen_${onloadPopup.id}`);
          if (!seen) {
            setCurrentPopup(onloadPopup);
            sessionStorage.setItem(`popup_seen_${onloadPopup.id}`, 'true');
          }
        } else {
          setCurrentPopup(onloadPopup);
        }
      }

      // Handle delay popups
      const delayPopups = activePopups.filter(p => p.trigger_type === 'delay');
      delayPopups.forEach(popup => {
        setTimeout(() => {
          if (popup.frequency === 'once_per_session') {
            const seen = sessionStorage.getItem(`popup_seen_${popup.id}`);
            if (!seen) {
              setCurrentPopup(popup);
              sessionStorage.setItem(`popup_seen_${popup.id}`, 'true');
            }
          } else {
            setCurrentPopup(popup);
          }
        }, (popup.trigger_delay || 0) * 1000);
      });
    }
  }, [activePopups]);

  const closePopup = () => {
    setCurrentPopup(null);
  };

  const renderPopup = () => {
    if (!currentPopup) return null;
    return <PopupRenderer popup={currentPopup} onClose={closePopup} />;
  };

  if (loading) {
    return (
      <div className="public-menu-loading">
        <div className="loading-spinner"></div>
        <p>Caricamento menu...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-menu-error">
        <h2>Errore</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <div className="public-menu-container">
      {renderPopup()}
      {loading && (
        <div className="loading-state">
          <p>Caricamento menu...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>Errore nel caricamento del menu: {error}</p>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Header with logo */}
          <header className="public-menu-header">
            {businessInfo?.logo_path && (
              <div className="logo-container">
                  <img 
                  src={`${API_BASE_URL}${businessInfo.logo_path}`} 
                  alt={businessInfo.name || 'Logo'}
                  className="business-logo"
                  onLoad={() => {
                    if (process.env.NODE_ENV !== 'production') {
                      console.log('Logo caricato con successo:', `${API_BASE_URL}${businessInfo.logo_path}`);
                    }
                  }}
                  onError={(e) => {
                    if (process.env.NODE_ENV !== 'production') {
                      console.error('Errore nel caricamento del logo:', `${API_BASE_URL}${businessInfo.logo_path}`);
                    }
                  }}
                />
              </div>
            )}
            <div className="business-name">
              Menu
            </div>
            {businessInfo?.description && (
              <div className="business-subtitle">
                <p className="business-description">{businessInfo.description}</p>
              </div>
            )}
            <div className="language-switch">
              <button
                type="button"
                className={language === 'it' ? 'active' : ''}
                onClick={() => {
                  setLanguage('it');
                  if (typeof window !== 'undefined') localStorage.setItem('menu-language', 'it');
                }}
                aria-label="Imposta lingua italiana"
              >
                IT
              </button>
              <span className="separator">|</span>
              <button
                type="button"
                className={language === 'en' ? 'active' : ''}
                onClick={() => {
                  setLanguage('en');
                  if (typeof window !== 'undefined') localStorage.setItem('menu-language', 'en');
                }}
                aria-label="Set English language"
              >
                EN
              </button>
            </div>
          </header>

          {/* Main content */}
          <main className="public-menu-content">
            <div className="categories-container">
              {visibleCustom && (
                <button
                  key={`custom-${visibleCustom.id}`}
                  className="category-button category-button--featured"
                  onClick={() => navigate(`/menu/custom/${visibleCustom.id}`)}
                >
                  <span className="category-text">
                    <span className="category-name">{visibleCustom.name}</span>
                    <span className="category-desc">
                      {language === 'en' ? 'Special menu' : 'Menu speciale'}
                    </span>
                  </span>
                  <span className="category-arrow" aria-hidden="true">›</span>
                </button>
              )}
              {categories.map((category) => {
                const desc = language === 'en'
                  ? (category.description_en || category.description)
                  : category.description;
                return (
                  <button
                    key={category.id}
                    className="category-button"
                    onClick={() => handleCategoryClick(category)}
                  >
                    <span className="category-text">
                      <span className="category-name">
                        {language === 'en' ? (category.name_en || category.name) : category.name}
                      </span>
                      {desc && <span className="category-desc">{desc}</span>}
                    </span>
                    <span className="category-arrow" aria-hidden="true">›</span>
                  </button>
                );
              })}

            </div>

            <p className="allergen-menu-notice">
              {language === 'en'
                ? 'If you have any food allergies or intolerances, please always inform our staff before ordering. Dishes may contain traces of allergens not listed. Full allergen information is available — just ask.'
                : 'In caso di allergie o intolleranze alimentari, La preghiamo di informare sempre il personale prima di ordinare. I piatti possono contenere tracce di allergeni non indicati. Le informazioni complete sono a disposizione: chieda al personale.'}
            </p>
          </main>

          {/* Footer (include il pannello asporto) */}
          <SharedFooter />
        </>
      )}
    </div>
  );
};

export default PublicMenu;